import { useCallback, useEffect, useState } from 'react';
import {
  previewInvitation,
  type InvitationPreview,
} from '@/services/invitation.service';

export function useInvitationPreview(token: string | null) {
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [loading, setLoading] = useState(token !== null);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!token) {
      setPreview(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      setPreview(await previewInvitation(token));
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught
          : new Error('Unable to load this invitation'),
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    let active = true;
    if (!token) {
      setPreview(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    previewInvitation(token)
      .then((next) => {
        if (active) setPreview(next);
      })
      .catch((caught: unknown) => {
        if (!active) return;
        setError(
          caught instanceof Error
            ? caught
            : new Error('Unable to load this invitation'),
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  return { preview, loading, error, refresh };
}
